import { 
  faArrowLeft, 
  faArrowRight,
  faMagic,
  faPlay,
  faImage,
  faVideo,
  faFileText,
  faClock,
  faCamera,
  faPalette,
  faCheckCircle,
  faPaintBrush,
  faMusic,
  faList,
  faMicrophone,
  faEllipsisH
} from '@fortawesome/free-solid-svg-icons';
import { Question } from '../types';

export const questions: Question[] = [
  {
    id: 'outputType',
    title: 'What do you want to create?',
    type: 'single',
    required: true,
    options: [
      { id: 'video', label: 'Video(s)', icon: faVideo, value: 'video content' },
      { id: 'image', label: 'Image(s)', icon: faImage, value: 'image content' },
      { id: 'music', label: 'Music Brief', icon: faMusic, value: 'a music composition brief' },
      { id: 'narration', label: 'Narration', icon: faMicrophone, value: 'narration and voice content' },
      { id: 'others', label: 'Other AI Content', icon: faEllipsisH, value: 'other AI-generated content' },
      { id: 'shotlist', label: 'Shot List', icon: faCamera, value: 'a detailed shot list' },
      { id: 'package', label: 'Complete Package', icon: faCheckCircle, value: 'a complete creative package with video, images, music, narration, and other content' }
    ]
  },
  {
    id: 'startType',
    title: 'How do you want to start?',
    type: 'single',
    required: true,
    options: [] // Will be dynamically populated based on output type
  },
  {
    id: 'quantity',
    title: 'How many do you want to create?',
    type: 'single',
    required: true,
    options: [
      { id: 'single', label: 'Just 1', value: 'single item' },
      { id: 'series-2-5', label: '2-5 (Short series)', value: 'a short series of 2-5 items' },
      { id: 'series-6-15', label: '6-15 (Medium series)', value: 'a medium series of 6-15 items' },
      { id: 'series-16-40', label: '16-40 (Long series)', value: 'a long series of 16-40 items' }
    ]
  },
  {
    id: 'uploadReference',
    title: 'Upload your reference media',
    type: 'upload-prompt',
    required: false,
    options: [] // Not used for upload prompt type
  },
  {
    id: 'contentType',
    title: 'What type of content?',
    type: 'single',
    required: true,
    options: [
      { id: 'commercial', label: 'Commercial', value: 'a commercial advertisement' },
      { id: 'bible', label: 'Bible Story', value: 'a biblical narrative scene' },
      { id: 'action', label: 'Action Scene', value: 'an action-packed sequence' },
      { id: 'documentary', label: 'Documentary', value: 'a documentary-style piece' },
      { id: 'tutorial', label: 'Tutorial', value: 'an educational tutorial' },
      { id: 'testimonial', label: 'Testimonial', value: 'a personal testimonial' }
    ]
  },
  {
    id: 'characterCount',
    title: 'How many main characters in your scene? (optional)',
    type: 'single',
    required: false,
    options: [
      { id: 'single', label: 'Just 1', icon: faMagic, value: 'single character focus' },
      { id: 'two', label: '2 Characters', icon: faMagic, value: 'two characters interacting' },
      { id: 'three', label: '3 Characters', icon: faMagic, value: 'three characters in scene' },
      { id: 'group', label: 'Group (4+)', icon: faMagic, value: 'multiple characters or crowd scene' }
    ]
  },
  {
    id: 'mood',
    title: 'What\'s the mood/style?',
    type: 'single',
    required: true,
    options: [
      { id: 'cinematic', label: 'Cinematic', icon: faPalette, value: 'with cinematic lighting and composition' },
      { id: 'playful', label: 'Playful', icon: faPalette, value: 'with a playful and energetic tone' },
      { id: 'dramatic', label: 'Dramatic', icon: faPalette, value: 'with dramatic tension and mood' },
      { id: 'minimalist', label: 'Minimalist', icon: faPalette, value: 'with clean, minimalist aesthetics' },
      { id: 'warm', label: 'Warm & Inviting', icon: faPalette, value: 'with warm, welcoming atmosphere' },
      { id: 'professional', label: 'Professional', icon: faPalette, value: 'with professional, polished look' }
    ]
  },
  {
    id: 'visualGenre',
    title: 'What genre/style matches your vision?',
    type: 'single',
    required: false,
    options: [
      { id: 'action', label: 'Action/Thriller', icon: faMagic, value: 'action and thriller cinematography' },
      { id: 'drama', label: 'Drama/Romance', icon: faMagic, value: 'dramatic and romantic cinematography' },
      { id: 'epic', label: 'Epic/Adventure', icon: faMagic, value: 'epic and adventure cinematography' },
      { id: 'fantasy', label: 'Fantasy/Sci-Fi', icon: faMagic, value: 'fantasy and sci-fi cinematography' },
      { id: 'horror', label: 'Horror/Psychological', icon: faMagic, value: 'horror and psychological cinematography' }
    ]
  },
  {
    id: 'sceneReference',
    title: 'Choose a visual reference (optional)',
    type: 'single',
    required: false,
    options: [
      // BIBLE STORY SCENES
      { id: 'david-goliath', label: 'David vs Goliath', value: 'epic biblical confrontation, dramatic lighting, vast battlefield, heroic underdog moment', contentType: 'bible', genre: 'epic' },
      { id: 'moses-red-sea', label: 'Moses Parting Red Sea', value: 'miraculous divine intervention, towering walls of water, dramatic sky, mass exodus scene', contentType: 'bible', genre: 'epic' },
      { id: 'last-supper', label: 'The Last Supper', value: 'intimate gathering, warm candlelight, emotional tension, classical composition', contentType: 'bible', genre: 'drama' },
      { id: 'noah-ark', label: 'Noah\'s Ark', value: 'stormy apocalyptic atmosphere, massive wooden vessel, animals gathering, divine judgment', contentType: 'bible', genre: 'epic' },
      
      // COMMERCIAL SCENES
      { id: 'apple-commercial', label: 'Apple: Clean & Minimal', value: 'pristine white backgrounds, perfect product lighting, minimalist composition, premium feel', contentType: 'commercial', genre: 'minimalist' },
      { id: 'nike-inspiration', label: 'Nike: Athletic Inspiration', value: 'dynamic sports action, motivational lighting, sweat and determination, aspirational mood', contentType: 'commercial', genre: 'action' },
      { id: 'coca-cola-family', label: 'Coca-Cola: Family Joy', value: 'warm golden lighting, genuine laughter, nostalgic atmosphere, heartwarming moments', contentType: 'commercial', genre: 'warm' },
      { id: 'luxury-car', label: 'Luxury Car Commercial', value: 'sleek automotive cinematography, dramatic shadows, premium materials, sophisticated mood', contentType: 'commercial', genre: 'cinematic' },
      
      // ACTION SCENES
      { id: 'john-wick-lobby', label: 'John Wick: Hotel Lobby', value: 'sleek modern interior with neon lighting, professional cinematography, dark atmospheric mood', contentType: 'action', genre: 'action' },
      { id: 'mad-max-chase', label: 'Mad Max: Desert Chase', value: 'gritty desert landscape, high-speed action, golden dusty atmosphere, dynamic camera movement', contentType: 'action', genre: 'action' },
      { id: 'matrix-bullet-time', label: 'The Matrix: Bullet Time', value: 'slow-motion cinematography, green tinted lighting, surreal atmosphere, rotating camera', contentType: 'action', genre: 'action' },
      
      // DOCUMENTARY SCENES
      { id: 'planet-earth', label: 'Planet Earth: Nature', value: 'stunning natural cinematography, patient wildlife observation, breathtaking landscapes, educational tone', contentType: 'documentary', genre: 'epic' },
      { id: 'social-dilemma', label: 'Social Media Documentary', value: 'modern tech aesthetic, interview setups, data visualization, contemporary concerns', contentType: 'documentary', genre: 'drama' },
      { id: 'historical-doc', label: 'Historical Documentary', value: 'archival footage style, dramatic reenactments, authoritative narration, educational gravitas', contentType: 'documentary', genre: 'drama' },
      
      // TUTORIAL SCENES  
      { id: 'cooking-tutorial', label: 'Cooking Tutorial', value: 'overhead cooking shots, warm kitchen lighting, step-by-step clarity, appetizing close-ups', contentType: 'tutorial', genre: 'warm' },
      { id: 'tech-tutorial', label: 'Tech Tutorial', value: 'clean screen recordings, modern UI design, clear instructional graphics, professional setup', contentType: 'tutorial', genre: 'professional' },
      { id: 'fitness-tutorial', label: 'Fitness Tutorial', value: 'energetic workout environment, motivational lighting, clear form demonstration, inspiring atmosphere', contentType: 'tutorial', genre: 'action' },
      
      // TESTIMONIAL SCENES
      { id: 'customer-testimonial', label: 'Customer Testimonial', value: 'authentic personal setting, soft natural lighting, genuine emotion, trustworthy atmosphere', contentType: 'testimonial', genre: 'warm' },
      { id: 'expert-testimonial', label: 'Expert Testimonial', value: 'professional office setting, authoritative lighting, credible background, confident delivery', contentType: 'testimonial', genre: 'professional' },
      { id: 'transformation-story', label: 'Transformation Story', value: 'before and after comparison, emotional journey, inspirational lighting, authentic moments', contentType: 'testimonial', genre: 'drama' }
    ]
  },
  {
    id: 'duration',
    title: 'How long should it be?',
    type: 'single',
    required: true,
    options: [
      { id: '15s', label: '15 seconds', icon: faClock, value: '15 seconds long' },
      { id: '30s', label: '30 seconds', icon: faClock, value: '30 seconds long' },
      { id: '1m', label: '1 minute', icon: faClock, value: '1 minute long' },
      { id: '2m', label: '2+ minutes', icon: faClock, value: '2 minutes or longer' }
    ]
  },
  {
    id: 'cameraStyle',
    title: 'What camera style?',
    type: 'single',
    required: true,
    options: [
      { id: 'closeup', label: 'Close-up shots', icon: faCamera, value: 'featuring close-up camera work' },
      { id: 'wide', label: 'Wide shots', icon: faCamera, value: 'with wide establishing shots' },
      { id: 'aerial', label: 'Aerial view', icon: faCamera, value: 'shot from aerial perspective' },
      { id: 'handheld', label: 'Handheld', icon: faCamera, value: 'with handheld camera movement' },
      { id: 'static', label: 'Static/Tripod', icon: faCamera, value: 'using stable, static shots' },
      { id: 'tracking', label: 'Tracking shots', icon: faCamera, value: 'with smooth tracking camera moves' }
    ]
  },
  {
    id: 'imageComposition',
    title: 'What composition style?',
    type: 'single',
    required: true,
    options: [
      { id: 'portrait', label: 'Portrait', icon: faImage, value: 'in portrait orientation' },
      { id: 'landscape', label: 'Landscape', icon: faImage, value: 'in landscape orientation' },
      { id: 'square', label: 'Square', icon: faImage, value: 'in square format' },
      { id: 'centered', label: 'Centered', icon: faImage, value: 'with centered composition' },
      { id: 'rule-of-thirds', label: 'Rule of Thirds', icon: faImage, value: 'following rule of thirds' },
      { id: 'dynamic', label: 'Dynamic Angle', icon: faImage, value: 'with dynamic angular composition' }
    ]
  },
  {
    id: 'artStyle',
    title: 'What art style?',
    type: 'single',
    required: true,
    options: [
      { id: '3d-pixar', label: '3D Pixar Style', icon: faPaintBrush, value: 'in 3D Pixar animation style with smooth textures and vibrant colors' },
      { id: '3d-realistic', label: '3D Realistic', icon: faPaintBrush, value: 'in photorealistic 3D rendering style' },
      { id: '2d-animation', label: '2D Animation', icon: faPaintBrush, value: 'in traditional 2D animation style' },
      { id: '2d-illustration', label: '2D Illustration', icon: faPaintBrush, value: 'in flat 2D illustration style' },
      { id: 'photorealistic', label: 'Photorealistic', icon: faPaintBrush, value: 'in photorealistic style like a real photograph' },
      { id: 'oil-painting', label: 'Oil Painting', icon: faPaintBrush, value: 'in classical oil painting style with rich textures' },
      { id: 'watercolor', label: 'Watercolor', icon: faPaintBrush, value: 'in watercolor painting style with soft, flowing colors' },
      { id: 'digital-art', label: 'Digital Art', icon: faPaintBrush, value: 'in modern digital art style' },
      { id: 'scratch-art', label: 'Scratch Art', icon: faPaintBrush, value: 'in scratch-art style with etched lines and textured details' }
    ]
  },
  // Cinematic Mode Questions
  {
    id: 'characterName',
    title: 'Character Name & Age',
    type: 'text',
    required: false,
    placeholder: 'e.g., Sarah, 25 years old',
    options: []
  },
  {
    id: 'characterFeatures',
    title: 'Character Physical Features',
    type: 'multiple',
    required: false,
    options: [
      { id: 'hair-blonde', label: 'Blonde Hair', value: 'blonde hair' },
      { id: 'hair-brunette', label: 'Brunette Hair', value: 'brunette hair' },
      { id: 'hair-black', label: 'Black Hair', value: 'black hair' },
      { id: 'eyes-blue', label: 'Blue Eyes', value: 'blue eyes' },
      { id: 'eyes-brown', label: 'Brown Eyes', value: 'brown eyes' },
      { id: 'eyes-green', label: 'Green Eyes', value: 'green eyes' },
      { id: 'eyes-hazel', label: 'Hazel Eyes', value: 'hazel eyes' },
      { id: 'build-athletic', label: 'Athletic Build', value: 'athletic build' },
      { id: 'build-slim', label: 'Slim Build', value: 'slim build' },
      { id: 'height-tall', label: 'Tall Height', value: 'tall height' },
      { id: 'height-average', label: 'Average Height', value: 'average height' }
    ]
  },
  {
    id: 'cameraSpecs',
    title: 'Camera Equipment',
    type: 'single',
    required: false,
    options: [
      { id: 'arri-alexa', label: 'Arri Alexa Mini LF', icon: faCamera, value: 'shot with Arri Alexa Mini LF' },
      { id: 'red-komodo', label: 'RED Komodo 6K', icon: faCamera, value: 'shot with RED Komodo 6K' },
      { id: 'canon-c70', label: 'Canon C70', icon: faCamera, value: 'shot with Canon C70' },
      { id: 'sony-fx9', label: 'Sony FX9', icon: faCamera, value: 'shot with Sony FX9' },
      { id: 'blackmagic', label: 'Blackmagic URSA', icon: faCamera, value: 'shot with Blackmagic URSA Mini' },
      { id: 'generic-pro', label: 'Professional Cinema', icon: faCamera, value: 'shot with professional cinema camera' }
    ]
  },
  {
    id: 'lensType',
    title: 'Lens Specification',
    type: 'single',
    required: false,
    options: [
      { id: 'wide-14mm', label: '14mm Wide Angle', value: '14mm wide angle lens' },
      { id: 'standard-35mm', label: '35mm Standard', value: '35mm standard lens' },
      { id: 'portrait-85mm', label: '85mm Portrait', value: '85mm portrait lens' },
      { id: 'tele-135mm', label: '135mm Telephoto', value: '135mm telephoto lens' },
      { id: 'macro-100mm', label: '100mm Macro', value: '100mm macro lens' },
      { id: 'anamorphic', label: 'Anamorphic Lens', value: 'anamorphic lens with cinematic bokeh' }
    ]
  },
  {
    id: 'shotStructure',
    title: 'Shot Structure Type',
    type: 'single',
    required: false,
    options: [
      { id: 'establishing', label: 'Establishing Wide', value: 'establishing wide shot showing full environment' },
      { id: 'medium-focus', label: 'Medium Focus', value: 'medium shot focusing on character and immediate surroundings' },
      { id: 'closeup-detail', label: 'Close-up Detail', value: 'close-up shot highlighting specific facial features or important objects' },
      { id: 'extreme-closeup', label: 'Extreme Close-up', value: 'extreme close-up capturing micro details and emotions' },
      { id: 'sequence-flow', label: 'Sequence Flow', value: 'structured sequence flowing from wide to close-up shots' }
    ]
  },
  {
    id: 'audioElements',
    title: 'Audio Characteristics',
    type: 'multiple',
    required: false,
    options: [
      { id: 'ambient-nature', label: 'Nature Ambience', value: 'natural ambient sounds' },
      { id: 'ambient-urban', label: 'Urban Ambience', value: 'urban city ambient sounds' },
      { id: 'ambient-indoor', label: 'Indoor Ambience', value: 'subtle indoor ambient sounds' },
      { id: 'voice-clear', label: 'Clear Voice', value: 'clear crisp voice delivery' },
      { id: 'voice-whisper', label: 'Whispered Voice', value: 'intimate whispered voice quality' },
      { id: 'voice-dramatic', label: 'Dramatic Voice', value: 'dramatic emotionally charged voice' },
      { id: 'acoustic-reverb', label: 'Reverb Effect', value: 'natural acoustic reverb' },
      { id: 'acoustic-intimate', label: 'Intimate Acoustics', value: 'close intimate acoustic quality' }
    ]
  },
  {
    id: 'environmentalSetup',
    title: 'Environmental Setup',
    type: 'multiple',
    required: false,
    options: [
      { id: 'lighting-natural', label: 'Natural Lighting', value: 'natural daylight lighting' },
      { id: 'lighting-golden', label: 'Golden Hour', value: 'warm golden hour lighting' },
      { id: 'lighting-dramatic', label: 'Dramatic Lighting', value: 'high contrast dramatic lighting' },
      { id: 'lighting-soft', label: 'Soft Lighting', value: 'soft diffused lighting setup' },
      { id: 'weather-clear', label: 'Clear Weather', value: 'clear atmospheric conditions' },
      { id: 'weather-moody', label: 'Moody Weather', value: 'atmospheric weather with mood' },
      { id: 'movement-static', label: 'Static Environment', value: 'calm static environmental setting' },
      { id: 'movement-dynamic', label: 'Dynamic Movement', value: 'environmental movement and energy' }
    ]
  },
  {
    id: 'keyElements',
    title: 'Key Thematic Elements',
    type: 'multiple',
    required: false,
    options: [
      { id: 'theme-mystery', label: 'Mystery', value: 'mysterious atmospheric elements' },
      { id: 'theme-romance', label: 'Romance', value: 'romantic emotional undertones' },
      { id: 'theme-action', label: 'Action', value: 'dynamic action elements' },
      { id: 'theme-drama', label: 'Drama', value: 'intense dramatic moments' },
      { id: 'theme-horror', label: 'Horror', value: 'suspenseful horror elements' },
      { id: 'theme-comedy', label: 'Comedy', value: 'light comedic elements' },
      { id: 'visual-color', label: 'Color Grading', value: 'cinematic color grading' },
      { id: 'visual-contrast', label: 'High Contrast', value: 'striking visual contrast' },
      { id: 'visual-symmetry', label: 'Symmetry', value: 'balanced symmetrical composition' },
      { id: 'visual-depth', label: 'Depth of Field', value: 'cinematic depth of field effects' }
    ]
  }
];
